import { useEffect, useState } from 'react';
import {
  useCreateGroupTier,
  useDeleteGroupTier,
  useGroupTiers,
  useUpdateGroupTier,
} from './use-group-tiers';
import { adopt, type TierDraft, toInput } from '../utils/group-tier-helpers';

export type { TierDraft } from '../utils/group-tier-helpers';

type Muts = {
  create: ReturnType<typeof useCreateGroupTier>;
  update: ReturnType<typeof useUpdateGroupTier>;
  remove: ReturnType<typeof useDeleteGroupTier>;
};

function useMuts(): Muts {
  return {
    create: useCreateGroupTier(),
    update: useUpdateGroupTier(),
    remove: useDeleteGroupTier(),
  };
}

async function applyDiff(
  drafts: TierDraft[],
  serverIds: Set<string>,
  m: Muts
): Promise<void> {
  const keepIds = new Set(drafts.filter((d) => !d.isNew).map((d) => d.id));
  for (const id of serverIds) {
    if (!keepIds.has(id)) await m.remove.mutateAsync(id);
  }
  for (let i = 0; i < drafts.length; i++) {
    const d = { ...drafts[i]!, sort_order: i + 1 };
    if (d.isNew) await m.create.mutateAsync(toInput(d));
    else await m.update.mutateAsync({ id: d.id, data: toInput(d) });
  }
}

type State = {
  drafts: TierDraft[];
  setTiers: (t: TierDraft[]) => void;
  save: () => Promise<void>;
  dirty: boolean;
  loading: boolean;
  saving: boolean;
};

type Ctx = {
  drafts: TierDraft[];
  setDrafts: (t: TierDraft[]) => void;
  setDirty: (b: boolean) => void;
  server: ReturnType<typeof useGroupTiers>['data'];
  m: Muts;
};

function makeActions(c: Ctx): Pick<State, 'setTiers' | 'save'> {
  return {
    setTiers: (t) => {
      c.setDirty(true);
      c.setDrafts(t);
    },
    save: async () => {
      const serverIds = new Set(c.server.map((s) => s.id));
      await applyDiff(c.drafts, serverIds, c.m);
      c.setDirty(false);
    },
  };
}

export function useGroupTierDrafts(): State {
  const { data: server, isLoading } = useGroupTiers();
  const [drafts, setDrafts] = useState<TierDraft[]>([]);
  const [dirty, setDirty] = useState(false);
  const m = useMuts();
  useEffect(() => {
    if (isLoading || dirty) return;
    setDrafts(adopt(server));
  }, [server, isLoading, dirty]);
  const actions = makeActions({ drafts, setDrafts, setDirty, server, m });
  return {
    drafts,
    ...actions,
    dirty,
    loading: isLoading,
    saving: m.create.isPending || m.update.isPending || m.remove.isPending,
  };
}
